import * as ko from 'knockout';
import * as localisationHelper from 'localisationHelper';
import * as Signals from 'signals';

// Services
import { ValidationService } from "Content/ts/Shared/Services/Validation/validationService";

// Helpers
import { StringFunctions } from "Content/ts/Shared/Functions/stringFunctions";

// Components
import 'Content/ts/shared/ConfirmationModal/confirmation-modal';

export interface BankDetailsParams {
    AccountNumber: KnockoutObservable<string>;
    SortCode: KnockoutObservable<string>;
    ValidationFields: KnockoutObservable<any>[];
    UpdateSignal: Signals.Signal;
    DirectDebitInitialise: Signals.Signal;
    AutoInitialise: boolean;
    IsRequired: KnockoutObservable<boolean>;
}

export class DDCanada {
    customMessages: {
        AccountNumber: string;
        AccountNumberMin: string;
        AccountNumberMax: string;
        AccountNumberInvalid: string;

        FinancialInstitution: string;
        FinancialInstitutionMin: string;
        FinancialInstitutionMax: string;
        FinancialInstitutionInvalid: string;

        BranchNumber: string;
        BranchNumberMin: string;
        BranchNumberMax: string;
        BranchNumberInvalid: string;
    };

    genericSortCode: KnockoutObservable<string>;

    accountNumber: KnockoutObservable<string>;
    financialInstitution: KnockoutObservable<string>;
    branchNumber: KnockoutObservable<string>;
    validationFields: KnockoutObservable<any>[];
    isRequired: KnockoutObservable<boolean>;
    autoInitialise: boolean;
    firstLoad: boolean;

    updateSignal: Signals.Signal;
    directDebitInitialise: Signals.Signal;

    constructor(params: BankDetailsParams) {
        this.accountNumber = params.AccountNumber;
        this.genericSortCode = params.SortCode;

        this.updateSignal = params.UpdateSignal;
        this.validationFields = params.ValidationFields;
        this.directDebitInitialise = params.DirectDebitInitialise;
        this.autoInitialise = params.AutoInitialise === undefined ? true : params.AutoInitialise;
        this.isRequired = params.IsRequired;

        this.financialInstitution = ko.observable(null);
        this.branchNumber = ko.observable(null);

        this.firstLoad = true;

        if (this.autoInitialise) {
            this.initialise();
        }
        else {
            this.directDebitInitialise.add(this.initialiseSignalMethod);
        }

        this.retrieveCustomMessages();
    }

    private initialiseSignalMethod = (enabled: boolean): void => {
        if (enabled) {
            this.initialise();
        }
        else {
            if (!this.firstLoad) {
                this.dispose();
            }
        }
    };

    private retrieveCustomMessages(): void {
        this.customMessages = {
            AccountNumber: StringFunctions.ToSentenceCase(localisationHelper.getString("GenericResources", "AccountNumber")),
            AccountNumberMin: localisationHelper.getString("Validation", "CanadaAccountNumberMin"),
            AccountNumberMax: localisationHelper.getString("Validation", "CanadaAccountNumberMax"),
            AccountNumberInvalid: localisationHelper.getString("Validation", "GenericAccountNumberInvalid"),

            FinancialInstitution: localisationHelper.getString("GenericResources", "DD_FinancialInstitution"),
            FinancialInstitutionMin: localisationHelper.getString("Validation", "FinancialInstitutionMin"),
            FinancialInstitutionMax: localisationHelper.getString("Validation", "FinancialInstitutionMax"),
            FinancialInstitutionInvalid: localisationHelper.getString("Validation", "FinancialInstitutionInvalid"),

            BranchNumber: localisationHelper.getString("GenericResources", "DD_BranchNumber"),
            BranchNumberMin: localisationHelper.getString("Validation", "CanadaBranchNumberMin"),
            BranchNumberMax: localisationHelper.getString("Validation", "CanadaBranchNumberMax"),
            BranchNumberInvalid: localisationHelper.getString("Validation", "GenericBranchNumberInvalid")
        }
    }

    private setupValidationFields(): void {
        ValidationService.AddValidationRule(this.accountNumber, this.validationFields, {
            minLength: {
                params: 7,
                message: this.customMessages.AccountNumberMin
            },
            maxLength: {
                params: 12,
                message: this.customMessages.AccountNumberMax
            },
            pattern: {
                message: this.customMessages.AccountNumberInvalid,
                params: /^[0-9]*$/
            }
        });

        // Comprised of the financial institution and branch number (in that order)
        ValidationService.AddValidationRule(this.genericSortCode, this.validationFields, {
            // these messages won't display to the user but causes some weird error in ko
            minLength: {
                params: 8,
                message: this.customMessages.AccountNumberMin
            },
            maxLength: {
                params: 8,
                message: this.customMessages.AccountNumberMax
            }
        });

        ValidationService.AddValidationRule(this.financialInstitution, this.validationFields, {
            minLength: {
                params: 3,
                message: this.customMessages.FinancialInstitutionMin
            },
            maxLength: {
                params: 3,
                message: this.customMessages.FinancialInstitutionMax
            },
            pattern: {
                params: /^[0-9]*$/,
                message: this.customMessages.FinancialInstitutionInvalid
            }
        });

        ValidationService.AddValidationRule(this.branchNumber, this.validationFields, {
            minLength: {
                params: 5,
                message: this.customMessages.BranchNumberMin
            },
            maxLength: {
                params: 5,
                message: this.customMessages.BranchNumberMax
            },
            pattern: {
                message: this.customMessages.BranchNumberInvalid,
                params: /^[0-9]*$/
            }
        });
    }

    private removeValidationFields(): void {
        ValidationService.RemoveValidationRule(this.accountNumber, this.validationFields);
        ValidationService.RemoveValidationRule(this.genericSortCode, this.validationFields);
        ValidationService.RemoveValidationRule(this.financialInstitution, this.validationFields);
        ValidationService.RemoveValidationRule(this.branchNumber, this.validationFields);
    }

    // These subscriptions need to be created after the validation so that
    // the validation can run before the subscription is fired. If not the knockout
    // validation will still be validating on the previous value
    private setupSubscriptions(): void {
        this.accountNumber.subscribe(() => {
            this.updateSignal.dispatch();
        });

        // comprised of the financial institution and branch number
        this.genericSortCode.subscribe(() => {
            this.updateSignal.dispatch();
        });

        this.financialInstitution.subscribe(() => {
            // check that the observables aren't null because ko will tostring null!
            if (this.financialInstitution() !== null && this.branchNumber() !== null) {
                this.genericSortCode(this.financialInstitution() + this.branchNumber());
            }
        });

        this.branchNumber.subscribe(() => {
            // check that the observables aren't null because ko will tostring null!
            if (this.financialInstitution() !== null && this.branchNumber() !== null) {
                this.genericSortCode(this.financialInstitution() + this.branchNumber());
            }
        });
    }

    private initialise(): void {
        this.setupValidationFields();
        this.setupSubscriptions();
        this.firstLoad = false;
    }

    public dispose(): void{
        this.removeValidationFields();
        this.directDebitInitialise.remove(this.initialiseSignalMethod);
    }
}

const componentName = 'dd-canada';
ko.components.unregister(componentName);
ko.components.register(componentName, {
    viewModel: DDCanada,
    template: { require: `text!/Themes/Default/Content/ts/Shared/DirectDebitDetails/${componentName}.html` }
});